import { API_URL, ApiError } from '../api';
import type { DocKey } from './keys';
import { accountState, importReceipt } from './sanitize';
import { isPlainObject } from './storage';
import type { AccountDocument, AccountState, BatchResult, ImportPayload, ImportReceipt, IntentOp, Me } from './types';

/**
 * The account half of the CommonGround API (CONTRACT 4.1–4.5), with the
 * signed-in user's WorkOS access token.
 *
 * Network failures, timeouts and 5xx answers are retried with backoff; nothing
 * else is, because resending a request the server has understood and refused
 * can't change its answer. Every call here is safe to repeat: intents are set
 * to absolute states, documents carry the version they were based on, and an
 * import is keyed by its `importId`.
 */

export class AccountApiError extends ApiError {
  /** The parsed error body, when there was one. */
  readonly body: unknown;
  /** A network failure, timeout or 5xx: worth trying again later. */
  readonly retryable: boolean;

  constructor(message: string, status: number, code: string | null, body: unknown, retryable: boolean) {
    super(message, status, code);
    this.name = 'AccountApiError';
    this.body = body;
    this.retryable = retryable;
  }
}

export const isRetryable = (err: unknown): boolean => !(err instanceof AccountApiError) || err.retryable;

/** The server's copy of a document, sent back with a 409 `VERSION_CONFLICT`. */
export function conflictCurrent(err: unknown): AccountDocument | null {
  if (!(err instanceof AccountApiError) || err.status !== 409 || !isPlainObject(err.body)) return null;
  const { body } = err;
  const error = isPlainObject(body.error) ? body.error : {};
  const details = isPlainObject(error.details) ? error.details : {};
  // The contract names `current` without placing it; accept each reading.
  const current = [details.current, error.current, body.current].find(isPlainObject);
  if (!current || typeof current.version !== 'number') return null;
  return { body: current.body ?? null, version: current.version };
}

export type GetAccessToken = (options?: { forceRefresh?: boolean }) => Promise<string>;

export interface AccountClientOptions {
  getAccessToken: GetAccessToken;
  baseUrl?: string;
  fetch?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
  /** Attempts after the first, for retryable failures. */
  retries?: number;
  timeoutMs?: number;
}

export interface AccountClient {
  getMe(): Promise<Me>;
  getState(): Promise<AccountState>;
  batchIntents(ops: IntentOp[]): Promise<BatchResult>;
  /** Rejects with a 409 `AccountApiError` when `baseVersion` is stale; see `conflictCurrent`. */
  putDocument(key: DocKey, body: unknown, baseVersion: number): Promise<{ version: number }>;
  postImport(importId: string, payload: ImportPayload): Promise<ImportReceipt>;
}

const DEFAULT_TIMEOUT_MS = 15_000;
/** The import is one transaction over up to 1 MB. */
const IMPORT_TIMEOUT_MS = 30_000;
const BACKOFF_MS = [600, 2_000, 5_000];

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function createAccountClient(options: AccountClientOptions): AccountClient {
  const base = (options.baseUrl ?? API_URL).replace(/\/+$/, '');
  const doFetch = options.fetch ?? ((input, init) => fetch(input, init));
  const sleep = options.sleep ?? defaultSleep;
  const retries = options.retries ?? BACKOFF_MS.length;

  async function token(forceRefresh: boolean): Promise<string> {
    try {
      return await options.getAccessToken(forceRefresh ? { forceRefresh: true } : undefined);
    } catch (err) {
      // AuthKit marks a refresh that may work later (offline, 5xx) as transient.
      const transient = Boolean(err && typeof err === 'object' && 'isTransient' in err && err.isTransient);
      throw new AccountApiError('Not signed in', transient ? 0 : 401, 'UNAUTHORIZED', null, transient);
    }
  }

  async function once<T>(method: string, path: string, body: unknown, timeoutMs: number, forceRefresh: boolean): Promise<T> {
    const accessToken = await token(forceRefresh);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let res: Response;
    try {
      res = await doFetch(`${base}${path}`, {
        method,
        signal: controller.signal,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch {
      // Offline, a timeout, or a CORS refusal: they look the same from here.
      throw new AccountApiError("We couldn't reach your account", 0, null, null, true);
    } finally {
      clearTimeout(timer);
    }

    if (res.ok) {
      if (res.status === 204) return undefined as T;
      try {
        return (await res.json()) as T;
      } catch {
        throw new AccountApiError('Unreadable answer from the account service', res.status, null, null, true);
      }
    }

    let parsed: unknown = null;
    try {
      parsed = await res.json();
    } catch {
      // Not the envelope: keep the status line.
    }
    const error = isPlainObject(parsed) && isPlainObject(parsed.error) ? parsed.error : {};
    throw new AccountApiError(
      typeof error.message === 'string' ? error.message : `${res.status} ${res.statusText}`.trim(),
      res.status,
      typeof error.code === 'string' ? error.code : null,
      parsed,
      res.status >= 500,
    );
  }

  async function request<T>(method: string, path: string, body?: unknown, timeoutMs = DEFAULT_TIMEOUT_MS): Promise<T> {
    let refreshed = false;
    let forceRefresh = false;
    for (let attempt = 0; ; attempt += 1) {
      try {
        return await once<T>(method, path, body, timeoutMs, forceRefresh);
      } catch (err) {
        forceRefresh = false;
        // A token that expired in flight: fetch a fresh one once, straight away.
        if (err instanceof AccountApiError && err.status === 401 && !refreshed) {
          refreshed = true;
          forceRefresh = true;
          attempt -= 1;
          continue;
        }
        if (!isRetryable(err) || attempt >= retries) throw err;
        const wait = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
        await sleep(wait + Math.round(Math.random() * wait * 0.3));
      }
    }
  }

  return {
    getMe: () => request<Me>('GET', '/me'),
    getState: async () => accountState(await request<unknown>('GET', '/me/state')),
    batchIntents: async (ops) => {
      const answer = await request<unknown>('POST', '/intents/me/batch', { ops });
      const results = isPlainObject(answer) && Array.isArray(answer.results) ? answer.results : [];
      return { results: results as BatchResult['results'] };
    },
    putDocument: async (key, body, baseVersion) => {
      const answer = await request<unknown>('PUT', `/me/documents/${key}`, { body, baseVersion });
      if (!isPlainObject(answer) || typeof answer.version !== 'number') {
        throw new AccountApiError('Unreadable answer from the account service', 200, null, answer, true);
      }
      return { version: answer.version };
    },
    postImport: async (importId, payload) =>
      importReceipt(await request<unknown>('POST', '/me/import', { importId, payload }, IMPORT_TIMEOUT_MS), importId),
  };
}
